import { Dynamic } from "solid-js/web";
import type { ComponentProps } from "solid-js";
import { cn } from "@boveda/shared";

/**
 * Bóveda brand mark — a blindado steel shield sealed with an emerald keyhole.
 * The steel body (deep blue-gray gradient) is the vault; the emerald keyhole is
 * the "sellado / seguro / solo tú" signal — the same duo used by the favicon and
 * boot loader. Brand stops are literal (not `currentColor`) so the mark reads
 * identically in light and dark. Gradient ids are suffixed to stay unique when
 * several logos render on the same page (e.g. sidebar + auth).
 */
let markCounter = 0;

function LogoMark(props: ComponentProps<"svg">) {
  const uid = (markCounter += 1);
  const steelId = `boveda-steel-${uid}`;
  const keyId = `boveda-key-${uid}`;
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <defs>
        <linearGradient id={steelId} x1="6" y1="2" x2="26" y2="30" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.42 0.04 255)" />
          <stop offset="1" stop-color="oklch(0.24 0.03 258)" />
        </linearGradient>
        <linearGradient id={keyId} x1="16" y1="9" x2="16" y2="22" gradientUnits="userSpaceOnUse">
          <stop stop-color="oklch(0.82 0.15 165)" />
          <stop offset="1" stop-color="oklch(0.68 0.15 163)" />
        </linearGradient>
      </defs>
      {/* Steel shield body */}
      <path
        d="M16 2.5l10 3.6v8.3c0 6.6-4.2 11.4-10 13.6-5.8-2.2-10-7-10-13.6V6.1L16 2.5z"
        fill={`url(#${steelId})`}
      />
      {/* Emerald edge highlight — the sealed rim */}
      <path
        d="M16 2.5l10 3.6v8.3c0 6.6-4.2 11.4-10 13.6-5.8-2.2-10-7-10-13.6V6.1L16 2.5z"
        fill="none"
        stroke="oklch(0.82 0.15 165)"
        stroke-opacity="0.35"
        stroke-width="0.8"
      />
      {/* Emerald keyhole */}
      <circle cx="16" cy="13.5" r="3.1" fill={`url(#${keyId})`} />
      <path d="M14.7 14.6h2.6l-0.85 5h-0.9z" fill={`url(#${keyId})`} />
    </svg>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0 drop-shadow-sm" />
          <span class="font-display text-xl font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/70">
            Bóveda
          </span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 shrink-0 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic
            component={wordmarkTag()}
            class="font-display text-3xl font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-br from-foreground to-foreground/80"
          >
            Bóveda
          </Dynamic>
        </>
      )}
    </div>
  );
}
